import { useAppDispatch } from '../store';
import { useEffect, useState } from 'react';
import { ApiResponse, BaseRepository } from './commonClasses';
import { AxiosError } from 'axios';
import { ACT_SET, TYPE_LIST } from './commonConstants';
import {
  IBaseModel,
  IListFetchParams,
  IListResponse,
} from './commonInterfaces';
import { CrudID, MODULE, Paths } from './commonTypes';
import { handleAxiosError } from '../utils';

interface UseListProps<T extends IBaseModel, Q extends IListFetchParams> {
  moduleName: MODULE;
  repository: BaseRepository<T, Q>;
  updateStore?: boolean;
  forcedParams?: Record<Paths<Q>, any>;
}

export interface UseListReturn<
  T extends IBaseModel,
  Q extends IListFetchParams,
> {
  fetchDataList: (manualParams?: Q) => Promise<void>;
  setFormElement: React.Dispatch<React.SetStateAction<T | undefined>>;
  setParams: React.Dispatch<React.SetStateAction<Q>>;
  dataList: IListResponse<T>;
  deleteElement: (id: CrudID) => Promise<void>;
  loading: boolean;
  formElement?: T;
  params?: Q;
  clearDataList?: () => void;
}

export const useList = <T extends IBaseModel, Q extends IListFetchParams>({
  moduleName,
  repository,
  updateStore = true,
  forcedParams,
}: UseListProps<T, Q>): UseListReturn<T, Q> => {
  const dispatch = useAppDispatch();
  const [loading, setloading] = useState<boolean>(false);
  const [formElement, setFormElement] = useState<T | undefined>(
    repository.element,
  );
  const [dataList, setDataList] = useState<IListResponse<T>>(repository.list);
  const [params, setParams] = useState<Q>({
    ...{ skip: 0, take: 10, search: '' },
    ...forcedParams,
  } as Q);

  const fetchDataList = async (manualParams?: Q) => {
    setloading(true);
    manualParams && setParams(manualParams);
    await repository
      .getMany(manualParams ?? params)
      .then((response: ApiResponse<IListResponse<T>>) => {
        response?.data && setData(response?.data as IListResponse<T>);
      })
      .catch((err: AxiosError) => handleAxiosError(err));
    setloading(false);
  };

  const deleteElement = async (id: CrudID) => {
    setloading(true);
    await repository
      .delete(id)
      .then(() => {
        setFormElement(undefined);
      })
      .catch((err: AxiosError) => handleAxiosError(err));
    setloading(false);
  };

  const setData = async (data: IListResponse<T>) => {
    updateStore &&
      (await dispatch({
        type: `${moduleName}/${TYPE_LIST}/${ACT_SET}`,
        payload: data,
      }));
    setDataList(data);
    repository.list = data;
  };

  const clearDataList = () => {
    setDataList({ data: [], totalCount: 0 });
    repository.list = { data: [], totalCount: 0 };
  };

  useEffect(() => {
    setParams({ ...params, ...forcedParams });
  }, [JSON.stringify(forcedParams)]);

  return {
    fetchDataList,
    dataList,
    setFormElement,
    formElement,
    deleteElement,
    setParams,
    loading,
    params,
    clearDataList,
  };
};
